import { z } from 'zod';
import { IsoDateTime, Uuid } from './common';
import { StoredFile } from './files';
import { Nozzle } from './slicer';
import { SupplyUnit } from './supplies';

const Centavos = z.number().int().min(0).max(100_000_000);
const Grams = z.number().min(0).max(1_000_000);

export const SpoolStatus = z.enum(['sealed', 'opened', 'loaded', 'empty']);
export type SpoolStatus = z.infer<typeof SpoolStatus>;
export const SPOOL_STATUS_LABELS: Record<SpoolStatus, string> = {
  sealed: 'Lacrado',
  opened: 'Aberto',
  loaded: 'Carregado',
  empty: 'Vazio',
};
/** "Estoque baixo" on a spool (board Carretel-Celular). */
export const SPOOL_LOW_GRAMS = 250;
/** Empty spool weight when the filament has none (Bambu Lab spools). */
export const DEFAULT_EMPTY_SPOOL_GRAMS = 250;
/** Spool grams needed for a print: estimate + 5% margin ("folga"). */
export const SPOOL_MARGIN = 1.05;

export const PurchasePayment = z.enum([
  'pix',
  'credit_card',
  'debit_card',
  'boleto',
  'cash',
  'other',
]);
export type PurchasePayment = z.infer<typeof PurchasePayment>;
export const PURCHASE_PAYMENT_LABELS: Record<PurchasePayment, string> = {
  pix: 'Pix',
  credit_card: 'Cartão de crédito',
  debit_card: 'Cartão de débito',
  boleto: 'Boleto',
  cash: 'Dinheiro',
  other: 'Outro',
};

export const SpoolMovementKind = z.enum([
  'purchase',
  'job_done',
  'job_failed',
  'weigh_in',
  'opened',
  'loaded',
  'unloaded',
  'emptied',
  'adjust',
]);

const SpoolFilament = z.object({
  id: Uuid,
  brand: z.string(),
  material: z.string(),
  name: z.string(),
  colorHex: z.string().nullable(),
  netWeightGrams: z.number().int(),
});

export const SpoolLocation = z.object({
  printerId: Uuid,
  printerName: z.string(),
  slot: z.string().describe('"1".."N" (AMS) or "ext".'),
  nozzle: Nozzle,
});

export const Spool = z
  .object({
    id: Uuid,
    code: z.string().describe('Label code, e.g. "C-0012".'),
    filament: SpoolFilament,
    status: SpoolStatus,
    initialGrams: z.number(),
    remainingGrams: z.number(),
    low: z.boolean().describe(`Under ${SPOOL_LOW_GRAMS} g and not empty.`),
    pricePaidCentavos: z.number().int(),
    valueCentavos: z.number().int().describe('Price paid × remaining ÷ initial grams.'),
    location: SpoolLocation.nullable().describe('Printer slot when loaded.'),
    locationNote: z.string().nullable().describe('Free text when on the shelf, e.g. "caixa seca".'),
    purchase: z
      .object({ id: Uuid, supplier: z.string().nullable(), purchasedOn: z.iso.date() })
      .nullable(),
    openedAt: IsoDateTime.nullable(),
    emptiedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Spool' });
export type Spool = z.infer<typeof Spool>;

export const SpoolMovement = z.object({
  id: Uuid,
  at: IsoDateTime,
  kind: SpoolMovementKind,
  grams: z.number().describe('Signed: − used, + added.'),
  note: z.string().nullable(),
  jobId: Uuid.nullable(),
  actor: z.string(),
});
export type SpoolMovement = z.infer<typeof SpoolMovement>;

export const SpoolNextJob = z.object({
  jobId: Uuid,
  number: z.string(),
  label: z.string(),
  units: z.number().int(),
  lineId: Uuid,
  estGrams: z.number(),
  needGrams: z.number().describe('Estimate + 5% folga.'),
  fix: z
    .object({ spoolId: Uuid, spoolCode: z.string(), sealed: z.boolean() })
    .nullable()
    .describe('Another spool of the same filament with enough grams ("Usar C-0016 na fila").'),
});
export const SpoolDetail = Spool.extend({
  movements: z.array(SpoolMovement),
  shortFor: SpoolNextJob.nullable().describe(
    'The first queued print this spool cannot finish ("Não dá para a próxima impressão da fila").',
  ),
}).meta({
  id: 'SpoolDetail',
});
export type SpoolDetail = z.infer<typeof SpoolDetail>;

export const SpoolList = z.object({ data: z.array(Spool), nextCursor: z.string().nullable() });
export const SpoolQuery = z.object({
  filamentId: Uuid.optional(),
  status: SpoolStatus.optional(),
  active: z.enum(['true', 'false']).optional().describe('Only spools that are not empty.'),
  printerId: Uuid.optional(),
  ids: z.string().max(4000).optional().describe('Comma-separated ids (e.g. for the label sheet).'),
  q: z.string().trim().max(40).optional().describe('Label code.'),
});

export const UpdateSpoolBody = z.object({
  locationNote: z.string().trim().max(120).nullable(),
});

export const WeighBody = z.object({
  grossGrams: Grams.describe('Spool + filament on the scale.'),
  confirm: z.boolean().default(false).describe('false = only preview the correction.'),
});
export const WeighResult = z.object({
  emptySpoolGrams: z.number(),
  emptySpoolDefault: z
    .boolean()
    .describe(
      `True when the filament has no empty-spool weight (${DEFAULT_EMPTY_SPOOL_GRAMS} g assumed).`,
    ),
  filamentGrams: z.number(),
  systemGrams: z.number(),
  diffGrams: z.number(),
  applied: z.boolean(),
  spool: Spool,
});

// ---- Purchases --------------------------------------------------------------------------------

const PurchaseHeader = z.object({
  supplier: z.string().trim().max(120).nullable().default(null),
  purchasedOn: z.iso.date(),
  paymentMethod: PurchasePayment.nullable().default(null),
  note: z.string().trim().max(1000).nullable().default(null),
  fileIds: z
    .array(Uuid)
    .max(10)
    .default([])
    .describe('Receipts or invoices uploaded with `POST /files` (kind `document`).'),
});

export const FilamentPurchaseBody = PurchaseHeader.extend({
  freightCentavos: Centavos.default(0).describe('Split across lines by net weight.'),
  updatePrices: z
    .boolean()
    .default(true)
    .describe(
      'Set each filament’s replacement price to this purchase’s price per kg with freight.',
    ),
  lines: z
    .array(
      z.object({
        filamentId: Uuid,
        spools: z.number().int().min(1).max(200),
        pricePerSpoolCentavos: Centavos.refine((v) => v > 0, 'Price must be positive'),
        netGrams: z
          .number()
          .int()
          .min(1)
          .max(100_000)
          .optional()
          .describe('Net grams per spool; defaults to the filament’s nominal weight.'),
      }),
    )
    .min(1)
    .max(50),
}).meta({ id: 'FilamentPurchaseBody' });
export type FilamentPurchaseBody = z.input<typeof FilamentPurchaseBody>;

export const SupplyPurchaseBody = PurchaseHeader.extend({
  freightCentavos: Centavos.default(0),
  lines: z
    .array(
      z.object({
        supplyId: Uuid,
        quantity: z.number().min(0.001).max(1_000_000),
        unitCostCentavos: Centavos.describe('Becomes the supply’s unit cost.'),
      }),
    )
    .min(1)
    .max(100),
}).meta({ id: 'SupplyPurchaseBody' });
export type SupplyPurchaseBody = z.input<typeof SupplyPurchaseBody>;

export const Purchase = z
  .object({
    id: Uuid,
    kind: z.enum(['filament', 'supplies']),
    supplier: z.string().nullable(),
    purchasedOn: z.iso.date(),
    paymentMethod: PurchasePayment.nullable(),
    freightCentavos: z.number().int(),
    totalCentavos: z.number().int(),
    note: z.string().nullable(),
    lines: z.array(
      z.object({
        filamentId: Uuid.nullable(),
        supplyId: Uuid.nullable(),
        name: z.string(),
        quantity: z.number(),
        unitPriceCentavos: z.number().int(),
        freightShareCentavos: z.number().int(),
        pricePerKgCentavos: z.number().int().nullable().describe('Filament: with freight.'),
        previousPricePerKgCentavos: z.number().int().nullable(),
      }),
    ),
    spools: z.array(z.object({ id: Uuid, code: z.string() })),
    files: z.array(StoredFile),
    expense: z
      .object({ id: Uuid, category: z.enum(['filament', 'supplies']) })
      .nullable()
      .describe('The expense in Despesas created with the purchase.'),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Purchase' });
export type Purchase = z.infer<typeof Purchase>;
export const PurchaseList = z.object({
  data: z.array(Purchase),
  nextCursor: z.string().nullable(),
});
export const PurchaseQuery = z.object({ kind: z.enum(['filament', 'supplies']).optional() });

export const FilamentPrice = z.object({
  pricePerKgCentavos: z.number().int(),
  effectiveOn: z.iso.date(),
  supplier: z.string().nullable(),
  purchaseId: Uuid.nullable(),
});
export const FilamentPriceList = z.object({ data: z.array(FilamentPrice) });

// ---- Printer slots ---------------------------------------------------------------------------

export const PrinterSlot = z.object({
  slot: z.string(),
  label: z.string().describe('"Slot 1" or "Externo".'),
  nozzle: Nozzle,
  spool: Spool.nullable(),
});
export type PrinterSlot = z.infer<typeof PrinterSlot>;
export const PrinterSlotList = z.object({ data: z.array(PrinterSlot) });
export const SlotParams = z.object({ id: Uuid, slot: z.string().regex(/^(\d{1,2}|ext)$/) });
export const LoadSlotBody = z.object({
  spoolId: Uuid.nullable().describe('null empties the slot (the spool goes back to the shelf).'),
});
export const UpdateSlotBody = z.object({ nozzle: Nozzle });

// ---- Supply stock -----------------------------------------------------------------------------

export const SupplyAdjustMotive = z.enum(['count', 'loss', 'use', 'other']);
export type SupplyAdjustMotive = z.infer<typeof SupplyAdjustMotive>;
export const SUPPLY_ADJUST_LABELS: Record<z.infer<typeof SupplyAdjustMotive>, string> = {
  count: 'Contagem',
  loss: 'Perda ou avaria',
  use: 'Uso fora do sistema',
  other: 'Outro',
};
export const SupplyAdjustBody = z.object({
  delta: z
    .number()
    .min(-1_000_000)
    .max(1_000_000)
    .refine((v) => v !== 0, 'Use − or + to change the stock'),
  motive: SupplyAdjustMotive,
  note: z.string().trim().max(300).nullable().default(null),
});
export const SupplyMovement = z.object({
  id: Uuid,
  at: IsoDateTime,
  kind: z.enum([
    'initial',
    'purchase',
    'job',
    'packing',
    'count',
    'loss',
    'use',
    'other',
    'return',
  ]),
  quantity: z.number(),
  note: z.string().nullable(),
  actor: z.string(),
});
export const SupplyMovementList = z.object({ data: z.array(SupplyMovement) });

// ---- Summary and shopping list -----------------------------------------------------------------

export const InventorySummary = z
  .object({
    valueCentavos: z.number().int().describe('Filament on hand at the price paid.'),
    availableGrams: z.number(),
    activeSpools: z.number().int(),
    lowFilaments: z.number().int(),
    lowSupplies: z.number().int(),
  })
  .meta({ id: 'InventorySummary' });

export const ShoppingList = z
  .object({
    filaments: z.array(
      z.object({
        filamentId: Uuid,
        name: z.string(),
        material: z.string(),
        stockGrams: z.number(),
        minimumGrams: z.number().int(),
        spools: z.number().int().describe('Spools to buy: set by hand, else the suggestion.'),
        suggested: z.number().int().describe('Spools to reach the minimum (0 when not low).'),
        adjusted: z.boolean().describe('`spools` was set by hand.'),
        pricePerSpoolCentavos: z.number().int(),
        subtotalCentavos: z.number().int(),
        lastSupplier: z.string().nullable(),
      }),
    ),
    supplies: z.array(
      z.object({
        supplyId: Uuid,
        name: z.string(),
        unit: SupplyUnit,
        quantityOnHand: z.number(),
        minimumQuantity: z.number(),
        quantity: z.number().describe('To buy: set by hand, else the suggestion.'),
        suggested: z.number().describe('Twice the minimum minus the stock (0 when not low).'),
        adjusted: z.boolean(),
        subtotalCentavos: z.number().int(),
      }),
    ),
    totalCentavos: z.number().int(),
    text: z.string().describe('The list ready to paste (WhatsApp, notes).'),
  })
  .meta({ id: 'ShoppingList' });
export type ShoppingList = z.infer<typeof ShoppingList>;
export const ShoppingEntryBody = z.object({
  quantity: z
    .number()
    .min(0)
    .max(10_000)
    .nullable()
    .describe('Spools (filament) or units (supply) to buy; null = back to the suggestion.'),
});

const gramsText = (g: number) => `${Math.round(g).toLocaleString('pt-BR')} g`;
const qtyText = (q: number) => q.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
const brl = (c: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(c / 100);

/** The shopping list as text to paste (same format in the API and after editing quantities). */
export function shoppingListText(list: Pick<ShoppingList, 'filaments' | 'supplies'>): string {
  const fil = list.filaments.filter((l) => l.spools > 0);
  const sup = list.supplies.filter((l) => l.quantity > 0);
  const total =
    fil.reduce((n, l) => n + l.pricePerSpoolCentavos * l.spools, 0) +
    sup.reduce((n, l) => n + l.subtotalCentavos, 0);
  return [
    'Lista de compras · Deizi3d',
    ...(fil.length ? ['', 'Filamentos:'] : []),
    ...fil.map(
      (l) =>
        `• ${l.name}: ${l.spools} ${l.spools === 1 ? 'carretel' : 'carretéis'} (tem ${gramsText(l.stockGrams)}, mínimo ${gramsText(l.minimumGrams)}) · ${brl(l.pricePerSpoolCentavos * l.spools)}${l.lastSupplier ? ` · ${l.lastSupplier}` : ''}`,
    ),
    ...(sup.length ? ['', 'Insumos:'] : []),
    ...sup.map(
      (l) =>
        `• ${l.name}: comprar ${qtyText(l.quantity)} (tem ${qtyText(l.quantityOnHand)}, mínimo ${qtyText(l.minimumQuantity)}) · ${brl(l.subtotalCentavos)}`,
    ),
    '',
    `Total estimado: ${brl(total)}`,
  ].join('\n');
}
